// components/WhyTrustUs.jsx
import React, { useState, useEffect, useRef } from 'react';
import '../CSS/LandingWhyTrustUs.css';
import callImage from '../../assets/call.jpg';
import EligibilityModal from '../../components/EligibilityModal'; // Chemin corrigé

const PmpWhyTrustUs = ({ scrollToCalendar, openEligibilityModal }) => {
  const [yearsCount, setYearsCount] = useState(0);
  const [professionalsCount, setProfessionalsCount] = useState(0);
  const [successRateCount, setSuccessRateCount] = useState(0);
  const [trainersCount, setTrainersCount] = useState(0);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const statsRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  const handleEligibilityComplete = (results) => {
    console.log('Résultats du questionnaire:', results);
    setShowEligibilityModal(false);
    
    // Rediriger vers l'inscription si recommandation PMP
    if (results.recommendation.certification === 'PMP') {
      scrollToCalendar();
    }
  };

  // Nouvelles données pour les statistiques avec meilleure structure
  const trustStats = [
    {
      id: 1,
      value: 10,
      suffix: "ans",
      mainLabel: "de Partenariat officiel avec le PMI",
      subLabel: "en tant que ATP Premium (anciennement REP)",
      isPercentage: false
    },
    {
      id: 2,
      value: 1000,
      suffix: "+",
      mainLabel: "professionnels formés",
      subLabel: "sur les métiers de la gestion de projet, du leadership et de la performance organisationnelle",
      isPercentage: false
    },
    {
      id: 3,
      value: 90,
      suffix: "%",
      mainLabel: "de réussite",
      subLabel: "aux certifications PMI",
      isPercentage: true
    },
    {
      id: 4,
      value: 6,
      suffix: "",
      mainLabel: "formateurs certifiés ATP Trainer",
      subLabel: "avec plus de 20+ d'expériences de pratique à l'international",
      isPercentage: false
    },
    {
      id: 5,
      value: 18,
      suffix: "ans",
      mainLabel: "d'expérience",
      subLabel: "2 antennes à Cotonou & Lomé et des interventions dans la sous-région",
      isPercentage: false
    },
    {
      id: 6,
      value: 'eTrainApp',
      suffix: "",
      mainLabel: "plateforme dédiée",
      subLabel: "Tous les supports, exercices et suivis réunis dans un parcours guidé",
      isPercentage: false
    },
  ];

  // Données pour les témoignages
  const testimonials = [
    {
      name: "Rissa ADEBO",
      certification: "Certifié PMP<sup>®</sup>",
      text: `Chez Inovact, j'ai bien apprécié la disponibilité ainsi que la flexibilité des coachs tout au long de la préparation pour ma certification PMP. Ils étaient toujours disponibles pour répondre à mes questions, et ce même à distance.
Pour ceux qui ont besoin d'un endroit fiable pour passer l'examen, leur local est disponible ainsi que toute l'équipe de soutien, pour permettre au candidat de passer l'examen dans les meilleures conditions. Je recommande vivement Inovact pour vous accompagner dans votre projet de certification PMP.
Melvin, Bertrand, Moudji et tout le reste de l'équipe, keep it up!`,
      avatar: "RA"
    },
    {
      name: "Ezekiel OCANSEY",
      certification: "Certifié PMP<sup>®</sup>",
      text: "Après une période de procrastination, j’ai finalement décidé de structurer ma préparation à l’examen PMP sur trois semaines. Ce choix était volontairement risqué, mais assumé. Plutôt que de multiplier les ressources, j’ai fait un choix stratégique : me concentrer presque exclusivement sur la pratique à travers le simulateur INOVACT et mes notes prise lors du cours.. Mon objectif n’était pas de consommer un maximum de contenu, mais de m’aligner sur la logique de l’examen. Ma méthode a été simple, mais exigeante : enchaîner des sessions de questions en conditions réelles, puis analyser systématiquement chaque erreur. Cette phase d’analyse a été déterminante. Elle m’a permis d’identifier des schémas récurrents, de retourner au PMBOK et aux cours au besoin.",
      avatar: "EO"
    },
    {
      name: "Maxime ADOKO",
      certification: "Certifié PMP<sup>®</sup>",
      text: `Je tiens à exprimer toute ma gratitude à INOVACT et à ses ATP Trainers pour la qualité de la formation et le coaching personnalisé qui m'ont accompagné tout au long de ce parcours exigeant.
📍 Étant basé au Bénin, j'ai fait le choix de la formation en présentiel au siège d'INOVACT. Au vu des résultats obtenus, je ne regrette absolument pas ce choix — un véritable projet de certification en collocalisation 😊
Un grand merci également à tous mes coéquipiers qui se préparent actuellement avec le soutien des formateurs d'INOVACT, toujours disponibles et bienveillants. 💪
Je leur souhaite à tous de décrocher leur certification avec brio et encore plus de performance !
✨ Je recommande vivement INOVACT à tous ceux qui se projettent vers la certification PMP®. Vous y trouverez rigueur, accompagnement de proximité et excellence pédagogique.`,
      avatar: "MA"
    }
  ];

  // Fonction d'animation de comptage
  const animateCounter = (targetValue, setter, duration = 1500) => {
    const startTime = Date.now();
    const startValue = 0;
    
    const updateCounter = () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(startValue + (targetValue - startValue) * easeOutQuart);
      
      setter(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    };
    
    requestAnimationFrame(updateCounter);
  };

  // Observer pour déclencher l'animation quand la section est visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCounter(10, setYearsCount, 2000);
            animateCounter(1000, setProfessionalsCount, 2000);
            animateCounter(90, setSuccessRateCount, 2000);
            animateCounter(6, setTrainersCount, 2000);
          }
        });
      },
      {
        threshold: 0.2, // Réduit à 20% de visibilité
        rootMargin: '0px 0px -100px 0px' // Détecte un peu plus tôt
      }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [hasAnimated]);

  // Déclencher l'animation au chargement si la section est déjà visible
  useEffect(() => {
    const checkIfVisible = () => {
      if (statsRef.current && !hasAnimated) {
        const rect = statsRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        
        // Vérifier si l'élément est visible dans la fenêtre
        const isVisible = (
          rect.top <= windowHeight * 0.8 && // 80% du viewport
          rect.bottom >= 0
        );
        
        if (isVisible) {
          setHasAnimated(true);
          animateCounter(10, setYearsCount, 2000);
          animateCounter(1000, setProfessionalsCount, 2000);
          animateCounter(90, setSuccessRateCount, 2000);
          animateCounter(6, setTrainersCount, 2000);
        }
      }
    };

    // Vérifier immédiatement au chargement
    setTimeout(checkIfVisible, 100);
    
    // Ajouter un écouteur de défilement pour les cas marginaux
    window.addEventListener('scroll', checkIfVisible);
    
    return () => {
      window.removeEventListener('scroll', checkIfVisible);
    };
  }, [hasAnimated]);

  return (
    <>
      <section className="landing-why-trust-us" id='confiance' style={{ marginBottom: "-80px" }} ref={statsRef}>
        <div className="landing-contenir">
          <h2 className="landing-section-title">Pourquoi nous faire confiance ?</h2>
          <p className="landing-section-subtitle">
            Avec plus de 10 ans d'expérience dans la préparation aux certifications PMI, 
            nous avons accompagné plus de 1000 professionnels.
          </p>
          
          {/* NOUVELLE GRILLE DE STATISTIQUES AVEC DISPOSITION AMÉLIORÉE */}
          <div className="landing-trust-stats-grid">
            {trustStats.map((stat) => (
              <div className="landing-trust-stat-card" key={stat.id}>
                <div className="landing-stat-number-container">
                  <span className="landing-stat-number">
                    {stat.id === 1 ? yearsCount : 
                     stat.id === 2 ? professionalsCount :
                     stat.id === 3 ? successRateCount :
                     stat.id === 4 ? trainersCount :
                     stat.value}
                  </span>
                  <span className="landing-stat-suffix">{stat.suffix}</span>
                </div>
                
                <div className="landing-stat-content">
                  {/* Ligne de séparation */}
                  <div className="landing-stat-divider"></div>
                  
                  {/* Contenu texte structuré */}
                  <div className="landing-stat-text-content">
                    <div className="landing-stat-main-text">
                      {stat.mainLabel.includes('**') ? (
                        <>
                          de <strong>Partenariat officiel avec le PMI</strong>
                        </>
                      ) : stat.mainLabel.includes('professionnels') ? (
                        <>
                          <strong>professionnels formés</strong>
                        </>
                      ) : (
                        <strong>{stat.mainLabel}</strong>
                      )}
                    </div>
                    
                    <p className="landing-stat-sub-text">{stat.subLabel}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* TÉMOIGNAGES */}
          <div className="landing-testimonials-container">
            <h3 className="landing-testimonials-title">Ils témoignent</h3>
            <div className="landing-testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div className="landing-testimonial-card" key={index}>
                  <p className="landing-testimonial-content">"{testimonial.text}"</p>
                  <div className="landing-testimonial-author">
                    <div className="landing-author-avatar">
                      <div className="landing-avatar-circle">{testimonial.avatar}</div>
                    </div>
                    <div className="landing-author-info">
                      <h4 className="landing-author-name">{testimonial.name}</h4>
                      <p className="landing-author-certification"
                        dangerouslySetInnerHTML={{ __html: testimonial.certification }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* BLOC: PASSEZ AU NIVEAU SUPÉRIEUR */}
        <div className="landing-next-level-section" style={{
          backgroundImage: `url(${callImage.src || callImage})`,
        }}>
          <div className="landing-next-level-overlay"></div>
          
          <h3 className="landing-next-level-title">
            Positionnez-vous dès maintenant parmi les meilleurs.
          </h3>
          
          <div className="landing-next-level-buttons">
            <button 
              className="landing-eligibility-btn"
              onClick={openEligibilityModal}
            >
              Vérifiez si vous êtes éligible
            </button>

            <button className="landing-join-btn" onClick={scrollToCalendar}>
              <span className="landing-join-text-desktop">Rejoignez notre prochain parcours PMP</span>
              <span className="landing-join-text-mobile">Rejoignez notre prochain parcours</span>
            </button>
          </div>
        </div>
      </section>

      {/* Modal d'éligibilité */}
      <EligibilityModal
        isOpen={showEligibilityModal}
        onClose={() => setShowEligibilityModal(false)}
        onComplete={handleEligibilityComplete}
      />

      <style jsx>{`
        /* Animation pour les chiffres */
        @keyframes landing-countUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .landing-stat-number {
          display: inline-block;
          animation: landing-countUp 0.5s ease-out forwards;
          opacity: 1; /* Changé de 0 à 1 pour être visible immédiatement */
        }
        
        /* NOUVELLE STRUCTURE DE LA GRILLE */
        .landing-trust-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          margin: 4rem 0 5rem;
        }
        
        .landing-trust-stat-card {
          background: white;
          border-radius: 12px;
          padding: 2.5rem 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border: 1px solid rgba(10, 55, 100, 0.1);
          display: flex;
          flex-direction: column;
          height: 100%;
          position: relative;
          overflow: hidden;
        }
        
        .landing-trust-stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          border-color: rgba(10, 55, 100, 0.2);
        }
        
        .landing-stat-number-container {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        
        .landing-stat-number {
          font-size: 3.5rem;
          font-weight: 800;
          color: #0a3764;
          line-height: 1;
          display: inline-block;
        }
        
        .landing-stat-suffix {
          font-size: 2.5rem;
          font-weight: 700;
          color: #0a3764;
          margin-left: 0.2rem;
        }
        
        .landing-stat-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .landing-stat-divider {
          width: 40px;
          height: 2px;
          background: linear-gradient(90deg, #0a3764, #94bf3e);
          margin: 0 auto 1.5rem;
          border-radius: 1px;
        }
        
        .landing-stat-text-content {
          text-align: center;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .landing-stat-main-text {
          font-size: 1.2rem;
          font-weight: 600;
          color: #2c3e50;
          line-height: 1.4;
          margin-bottom: 0.8rem;
        }
        
        .landing-stat-main-text strong {
          font-weight: 700;
          color: #0a3764;
        }
        
        .landing-stat-sub-text {
          font-size: 0.95rem;
          color: #666;
          line-height: 1.5;
          margin: 0;
          flex: 1;
        }
        
        /* Effet de séparation subtile comme dans l'image */
        .landing-trust-stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #0a3764, #94bf3e);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .landing-trust-stat-card:hover::before {
          opacity: 1;
        }
        
        /* Responsive pour la grille */
        @media (max-width: 1024px) {
          .landing-trust-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 25px;
          }
        }
        
        @media (max-width: 768px) {
          .landing-trust-stats-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .landing-trust-stat-card {
            padding: 2rem 1.5rem;
          }
          
          .landing-stat-number {
            font-size: 3rem;
          }
          
          .landing-stat-suffix {
            font-size: 2rem;
          }
          
          .landing-stat-main-text {
            font-size: 1.1rem;
          }
          
          .landing-stat-sub-text {
            font-size: 0.9rem;
          }
        }
        
        /* Styles pour le bloc next-level */
        .landing-next-level-section {
          margin: 4rem auto 2rem;
          padding: 3rem;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          color: white;
          border-radius: 0px;
          text-align: center;
          position: relative;
          overflow: hidden;
          max-width: 1300px;
        }
        
        .landing-next-level-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.4);
          z-index: 0;
        }
        
        .landing-next-level-title {
          font-size: 2rem;
          margin-bottom: 1.5rem;
          font-weight: 700;
          position: relative;
          z-index: 1;
          color: white;
        }
        
        .landing-next-level-buttons {
          position: relative;
          z-index: 1;
          margin-top: 2rem;
          display: flex;
          justify-content: center;
          gap: 2rem;
          flex-wrap: wrap;
          align-items: stretch;
        }
        
        .landing-eligibility-btn, .landing-join-btn {
          padding: 1rem 2.5rem;
          font-size: 1.1rem;
          font-weight: 600;
          border: none;
          border-radius: 0px;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 250px;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          flex: 1;
          min-height: 60px;
          box-sizing: border-box;
          line-height: 1.2;
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        .landing-eligibility-btn {
          background-color: #94bf3e;
          color: white;
          border: 2px solid #94bf3e;
        }
        
        .landing-eligibility-btn:hover {
          background-color: #94bf3e;
          border-color: #f0f0f0;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(148, 191, 62, 0.3);
        }
        
        .landing-join-btn {
          background-color: #0a3764;
          color: white;
          border: 2px solid #0a3764;
        }
        
        .landing-join-btn:hover {
          background-color: #09407e;
          border-color: #09407e;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(10, 55, 100, 0.3);
        }
        
        /* Styles responsive pour les boutons */
        .landing-join-text-desktop {
          display: inline;
        }
        
        .landing-join-text-mobile {
          display: none;
        }
        
        @media (max-width: 768px) {
          .landing-join-text-desktop {
            display: none;
          }
          
          .landing-join-text-mobile {
            display: inline;
          }
        }
        
        /* Responsive pour mobile */
        @media (max-width: 480px) {
          .landing-trust-stat-card {
            padding: 1.5rem 1rem;
          }
          
          .landing-stat-number {
            font-size: 2.5rem;
          }
          
          .landing-stat-suffix {
            font-size: 1.8rem;
          }
          
          .landing-stat-main-text {
            font-size: 1rem;
          }
          
          .landing-stat-sub-text {
            font-size: 0.85rem;
          }
        }
        
        @media (max-width: 360px) {
          .landing-stat-number {
            font-size: 2.2rem;
          }
          
          .landing-stat-suffix {
            font-size: 1.6rem;
          }
        }
      `}</style>
    </>
  );
};

export default PmpWhyTrustUs;